/**
 * Every value hela may send in the `x-action-name` header.
 *
 * The header is what the backend records against a request: the gateway's response interceptor
 * reads it, the analyzer stores it, and the Developers -> API Requests screen builds its action
 * filter from the matching list in qgen-backend (`pkg/constants/actions/actions.go`). A name that
 * is not in that list is still recorded, but it cannot be picked in the filter and a query for it
 * is rejected - so the two sides are a contract, and `scripts/actions/check-action-names.cjs`
 * reports what this catalogue has and the backend does not.
 *
 * These strings were inline literals at ~110 call sites, which is how 24 of them came to be
 * unknown to the backend. Spellings are therefore preserved exactly as they were sent, however
 * inconsistent: they are the values already stored against historical requests, and renaming one
 * takes those rows out of reach of the filter. Add a name here and to the backend list in the same
 * change; never build one from a translated string, which would make the recorded value depend on
 * the viewer's locale.
 */
export const ACTION_NAMES = {
	ACKNOWLEDGE_CHANGES_ACTION: 'Acknowledge Changes Action',
	ADD_NOTE: 'Add Note',
	ANALYZE_PROOF_OF_ADDRESS: 'Analyze proof of address',
	APPLY_FILTER: 'Apply Filter',
	APPLY_FILTER_ON_CASES: 'Apply Filter on cases',
	BATCH_CERTIFICATE_DOWNLOAD: 'Batch Certificate Download',
	BATCH_PROCESS_START: 'Batch Process Start',
	BATCH_RECORDS_UPLOAD: 'Batch Records Upload',
	BATCH_SEARCH_CREATION: 'Batch Search Creation',
	CHECK_AML_LINK_WITH_ENTITY: 'Check aml link with entity',
	CHECK_CASE_STATUS_USAGE: 'Check case status usage',
	CREATED_CASE: 'Created Case',
	CREATE_CASE_STATUS: 'Create Case Status',
	CREATE_CASE_STATUS_SET: 'Create case status set',
	CREATE_CASE_TYPE: 'Create Case Type',
	CREATE_CUSTOM_FIELD_SET: 'Create custom field set',
	CREATE_ENTITY: 'Create Entity',
	CREATE_FORM_TEMPLATE: 'Create Form Template',
	CREATE_SEARCH: 'Create Search',
	DELETE_A_SEARCH: 'Delete a Search',
	DELETE_CASES: 'Delete cases',
	DELETE_CASE_STATUS: 'Delete Case Status',
	DELETE_CASE_STATUS_SET: 'Delete case status set',
	DELETE_CASE_TYPE: 'Delete Case Type',
	DELETE_CUSTOM_FIELD_SET: 'Delete custom field set',
	DELETE_DOCUMENTS: 'Delete Documents',
	DELETE_FORM_TEMPLATE: 'Delete Form Template',
	DELETE_PROOF_OF_ADDRESS_SUBMISSIONS: 'Delete proof of address submissions',
	DELETE_SEARCHES: 'Delete Searches',
	DOWNLOAD_CSV: 'Download CSV',
	DOWNLOAD_ENTITY_DOCUMENTS: 'Download Entity Documents',
	DOWNLOAD_ENTITY_FILE: 'Download entity file',
	DOWNLOAD_EXCEL_FILE_XSLX_FORMAT: 'Download Excel File (XSLX Format)',
	DOWNLOAD_PDF_SEARCH_DETAILS: 'Download PDF (Search Details)',
	DOWNLOAD_PDF_SEARCH_HISTORY: 'Download PDF (Search History)',
	DOWNLOAD_PDF_SEARCH_RESULTS: 'Download PDF (Search Results)',
	FIND_CASES_USING_RESOURCE: 'Find cases using resource',
	FIND_CASE_STATUS: 'Find case status',
	FIND_CASE_STATUSES: 'Find case statuses',
	FIND_CASE_STATUS_SET: 'Find case status set',
	FIND_CASE_STATUS_SETS: 'Find case status sets',
	FIND_CASE_TYPE: 'Find case type',
	FIND_CUSTOM_FIELD_SETS: 'Find custom field sets',
	GET_ALL_HISTORY: 'Get All History',
	GET_BATCH_INFORMATION: 'Get Batch Information',
	GET_BATCH_RECORDS: 'Get Batch Records',
	GET_DASHBOARD_AML_COUNT: 'Get Dashboard AML Count',
	GET_SEARCH_CRITERIA: 'Get Search criteria',
	LINK_AML_SEARCH_TO_ENTITY: 'Link aml search to entity',
	LINK_PROOF_OF_ADDRESS_SUBMISSION_TO_ENTITY:
		'Link proof of address submission to entity',
	LOADED_CASE_DETAILS: 'Loaded Case Details',
	LOADING_BATCH_HISTORY: 'Loading batch history',
	LOADING_SEARCH_DETAILS: 'Loading search details',
	LOAD_ALL_CASES: 'Load all cases',
	LOAD_AML_SEARCH_RESULT_ON_A_CASE_CHECK:
		'Load AML search result on a case check',
	LOAD_CASE_REVISIONS: 'Load Case Revisions',
	LOAD_ENTITIES_FOR_ENTITY_PICKER: 'Load entities for entity picker',
	LOAD_IMAGE_PREVIEW_ON_DOCUMENTS: 'Load image preview on documents',
	MONITOR: 'Monitor',
	MONITOR_CHANGED: 'Monitor changed',
	OVERRIDE_FORM_SCORE: 'Override Form Score',
	PAGINATE_HITS: 'Paginate hits',
	REFRESH_SEARCH: 'Refresh Search',
	RESET_FILTER: 'Reset Filter',
	RESET_SEARCH_FORM: 'Reset Search Form',
	RETRY_BATCH_SEARCH: 'Retry Batch Search',
	RETRY_CASE_AML_REQUEST: 'Retry case aml request',
	REVISIONS_OF_CASE: 'Revisions of case',
	REVISIONS_OF_COMPANY_DOCUMENT: 'Revisions of company document',
	REVISIONS_OF_PERSON_DOCUMENT: 'Revisions of person document',
	SEARCH_ENTITIES_FOR_ENTITY_PICKER: 'Search entities for entity picker',
	SET_DEFAULT_CASE_STATUS: 'Set default case status',
	SUBMIT_FORM: 'Submit Form',
	TERMINATE_CFM_SESSION: 'Terminate CFM session',
	UNLINK_AML_SEARCH_TO_ENTITY: 'unLink aml search to entity',
	UNLINK_PROOF_OF_ADDRESS_SUBMISSION_FROM_ENTITY:
		'Unlink proof of address submission from entity',
	UPDATE_CASE: 'Update Case',
	// UPDATE_CASE_STATUS sets the status of a case; UPDATE_CASE_STATUS_SETTING edits a case
	// status definition on the settings screen. Two different actions whose labels differ only
	// by case, which is how they were already being sent.
	UPDATE_CASE_STATUS: 'Update Case Status',
	UPDATE_CASE_STATUS_SET: 'Update case status set',
	UPDATE_CASE_STATUS_SETTING: 'Update Case Status Setting',
	UPDATE_CASE_TYPE: 'Update Case Type',
	UPDATE_CUSTOM_FIELD_SET: 'Update custom field set',
	UPDATE_ENTITY: 'Update Entity',
	UPDATE_CLIENT_REFERENCE: 'Update Client Reference',
	UPDATE_ENTITY_STATUS: 'Update entity status',
	UPDATE_FILE_DETAILS: 'Update File Details',
	UPDATE_FORM_TEMPLATE: 'Update Form Template',
	UPDATE_STATUS: 'Update Status',
	UPDATE_WHITE_LIST: 'Update White List',
	UPLOAD_FILE_DOCUMENT: 'Upload File Document',
} as const;

export type ActionName = (typeof ACTION_NAMES)[keyof typeof ACTION_NAMES];
